
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Transformer } from '../../../../../react-konva';


import {
  NODES_TRANSFORMER_ID,
  POINTER_ICONS,
  TOOLS_IDS,
} from '../../../utils/constants';
import { useStore } from '../../../hooks';
import { CHANGE_POINTER_ICON, ENABLE_TEXT_CONTENT_EDIT } from '../../../actions';
import { focusTextContentField } from '../../../hooks/textContentFieldFocus';
import { getSelectionTransformerAppearance } from '../../../utils/getSelectionTransformerAppearance';
import expandSelectionIdsForTransformer from '../../../utils/expandSelectionIdsForTransformer';
import { getAnnotationStackOrderKey } from '../../../utils/reorderAnnotationsInStack';
import {
  detachCornerRotateHandle,
  syncCornerRotateHandle,
} from '../../../utils/cornerRotateHandle';

const EMPTY_ANCHORS = [];

const resolveTransformerNodes = (designLayer, transformerNodeIds, annotations) => {
  if (!designLayer?.findOne || transformerNodeIds.length === 0) {
    return [];
  }

  return transformerNodeIds
    .map((selectionId) => designLayer.findOne(`#${selectionId}`))
    .filter((node) => {
      if (!node || !node.getStage()) {
        return false;
      }
      return Boolean(annotations[node.id()]);
    });
};

const NodesTransformer = () => {
  const {
    selectionsIds = [],
    annotations = {},
    theme,
    designLayer,
    dispatch,
    config: { useCloudimage },
  } = useStore();

  const transformerNodeIds = useMemo(
    () => expandSelectionIdsForTransformer(selectionsIds, annotations),
    [annotations, selectionsIds],
  );

  const annotationStackKey = useMemo(
    () => getAnnotationStackOrderKey(annotations),
    [annotations],
  );

  const [transformerNodes, setTransformerNodes] = useState([]);

  const transformerRef = useRef(null);

  const appearance = useMemo(
    () => getSelectionTransformerAppearance(selectionsIds, annotations, theme.palette),
    [annotations, selectionsIds, theme.palette],
  );

  const selectionIsLocked = appearance.state === 'locked';

  const rotateEnabled =
    appearance.rotateEnabled && !useCloudimage && appearance.resizeEnabled;

  const resizeAnchors = useMemo(() => {
    if (!appearance.showAnchors) {
      return EMPTY_ANCHORS;
    }
    return undefined;
  }, [appearance.showAnchors]);

  const syncCornerRotate = useCallback(
    (transformer) => {
      syncCornerRotateHandle(transformer, {
        visible: rotateEnabled && appearance.showAnchors,
        anchorFill: appearance.anchorFill,
        anchorStroke: appearance.anchorStroke,
        anchorSize: appearance.showAnchors ? 14 : 0,
      });
    },
    [
      appearance.anchorFill,
      appearance.anchorStroke,
      appearance.showAnchors,
      rotateEnabled,
    ],
  );

  useLayoutEffect(() => {
    if (transformerNodeIds.length === 0) {
      setTransformerNodes([]);
      return undefined;
    }

    let cancelled = false;

    const attachNodes = (nodes) => {
      if (cancelled) {
        return;
      }
      setTransformerNodes(nodes);
      const transformer = transformerRef.current;
      if (!transformer) {
        return;
      }
      transformer.nodes(nodes);
      const back = transformer.findOne('.back');
      if (back) {
        back.draggable(!selectionIsLocked);
      }
      syncCornerRotate(transformer);
      transformer.getLayer()?.batchDraw();
    };

    const syncNodes = () => {
      attachNodes(
        resolveTransformerNodes(designLayer, transformerNodeIds, annotations),
      );
    };

    syncNodes();
    const raf1 = requestAnimationFrame(() => {
      syncNodes();
      if (!cancelled) {
        requestAnimationFrame(syncNodes);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
  }, [
    annotationStackKey,
    annotations,
    designLayer,
    selectionIsLocked,
    syncCornerRotate,
    transformerNodeIds,
  ]);

  useLayoutEffect(
    () => () => {
      detachCornerRotateHandle(transformerRef.current);
    },
    [],
  );

  const changePointerIconToMove = () => {
    dispatch({
      type: CHANGE_POINTER_ICON,
      payload: {
        pointerCssIcon: POINTER_ICONS.MOVE,
      },
    });
  };

  const changePointerIconToDraw = () => {
    dispatch({
      type: CHANGE_POINTER_ICON,
      payload: {
        pointerCssIcon: POINTER_ICONS.DRAW,
      },
    });
  };

  const enableTextContentChangeOnDblClick = () => {
    if (selectionIsLocked || !appearance.resizeEnabled) {
      return;
    }
    const textNode = transformerNodes.find(
      (node) => node.name() === TOOLS_IDS.TEXT,
    );
    if (!textNode) {
      return;
    }
    dispatch({
      type: ENABLE_TEXT_CONTENT_EDIT,
      payload: {
        textIdOfEditableContent: textNode.id(),
      },
    });
    focusTextContentField();
  };

  if (transformerNodeIds.length === 0) {
    return null;
  }

  // ALT is used to center scaling
  // SHIFT is used to scaling with keeping ratio
  return (
    <Transformer
      ref={transformerRef}
      id={NODES_TRANSFORMER_ID}
      centeredScaling={false}
      rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
      nodes={transformerNodes}
      rotateAnchorOffset={30}
      anchorSize={appearance.showAnchors ? 14 : 0}
      anchorCornerRadius={7}
      borderEnabled
      padding={
        transformerNodes.length === 1
          ? transformerNodes[0]?.attrs?.padding ?? 1
          : 1
      }
      ignoreStroke={false}
      anchorStroke={appearance.anchorStroke}
      anchorFill={appearance.anchorFill}
      anchorStrokeWidth={8}
      borderStroke={appearance.borderStroke}
      borderStrokeWidth={2}
      borderDash={[4]}
      resizeEnabled={appearance.resizeEnabled}
      rotateEnabled={rotateEnabled}
      enabledAnchors={resizeAnchors}
      onMouseOver={appearance.resizeEnabled ? changePointerIconToMove : undefined}
      onMouseLeave={appearance.resizeEnabled ? changePointerIconToDraw : undefined}
      onDblClick={enableTextContentChangeOnDblClick}
      onDblTap={enableTextContentChangeOnDblClick}
      flipEnabled={appearance.resizeEnabled}
      shouldOverdrawWholeArea
    />
  );
};

export default NodesTransformer;
